import React, { memo, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLocalStorage } from "usehooks-ts";
import useAuthContext from "../../providers/authProvider";
import Cookies from "js-cookie";
import Select from "react-select";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

import { organizationsClubs, getClubGroups } from "../../services/organizations";
import { updateClubGroup } from "../../services/organizations";

import { selectNumberStyles } from "../../components/shared/styles";

const OrganizationProfile = () => {
  const { organization } = useAuthContext();
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const { clubs } = await organizationsClubs();
      const { groups } = await getClubGroups();
      const groupedTeams = groups.map(({ id, name, position }) => ({
        id,
        name,
        clubs: clubs.filter((t) => t.club_group_id === id),
        position,
      }));
      groupedTeams.push({
        name: "",
        clubs: clubs.filter((t) => !t.club_group_id),
      });
      groupedTeams.sort((a, b) => Number(a.position) - Number(b.position));
      setGroups(groupedTeams);
    };

    fetchData();
  }, []);

  const onDragEnd = async ({ source, destination }) => {
    if (!destination || (source.droppableId === destination.droppableId && source.index === destination.index)) return;

    const filteredGroups = groups.filter((group) => group.id);
    const getPosition = (index) => Number(filteredGroups[index]?.position) || 0;

    let newPosition;
    if (destination.index === 0) {
      newPosition = getPosition(destination.index) / 2;
    } else if (destination.index === filteredGroups.length - 1) {
      newPosition = getPosition(filteredGroups.length - 1) + 1;
    } else {
      newPosition = (getPosition(destination.index - 1) + getPosition(destination.index)) / 2;
    }

    const [removed] = groups.splice(source.index, 1);
    groups.splice(destination.index, 0, removed);

    try {
      await updateClubGroup(removed.id, {
        name: removed.name,
        position: newPosition.toFixed(3),
      });
    } catch (error) {
      console.error("Error updating group positions:", error);
    }
  };

  return (
    <>
      <div className="club-home">
        <div className="club-home-details">
          <div className="club-home-details-image">
            <img src={organization?.logo} alt="Club Logo" />
          </div>
          <div className="club-home-details-description">
            <h2>{organization?.name}</h2>
            <hr />

            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="group">
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="club-home-details-description-teams"
                  >
                    {groups.filter((g) => g.id).map((group, index) => (
                        <ClubGroup
                          key={group.id}
                          group={group}
                          index={index}
                          draggable
                        />
                      ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            <div className="club-home-details-description-teams">
              {groups.filter((g) => !g.id).map((group, index) => ( <ClubGroup key={index} group={group} index={index} />))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default memo(OrganizationProfile);

const ClubGroup = ({ group, index, draggable = false }) => {
  const { id, name, clubs } = group;
  const { setClub } = useAuthContext();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [visibleGroupCounts, setVisibleGroupCounts] = useLocalStorage("visibleGroupCounts", {});
  const visibleTeams = visibleGroupCounts[id] || 10;

  const options = [
    { value: 10, label: "10" },
    { value: 25, label: "25" },
    { value: 50, label: "50" },
    { value: 75, label: "75" },
    { value: 1000, label: t("common.all") },
  ];

  const selectClub = (clubId) => {
    Cookies.set("club_id", clubId);
    setClub();
    navigate(`/club_admin/club/${clubId}`);
  };

  const content = (dragProps = {}) => (
    <div className="club-block" ref={dragProps.innerRef} {...dragProps.draggableProps}>
      <div className="club-home-details-description-teams-header">
        <div className="team-group-name">
          {draggable && (
            <i {...dragProps.dragHandleProps} className="icon-profile icon-options-vertical"/>
          )}
          <h4>{name || t("common.clubs")}</h4>
        </div>
        <div className="team-group-controls">
          <Select
            value={options.find((option) => option.value === visibleTeams)}
            onChange={(selectedOption) =>
              setVisibleGroupCounts((prev) => ({
                ...prev,
                [id]: Number(selectedOption.value),
              }))
            }
            options={options}
            isSearchable={false}
            styles={selectNumberStyles}
          />
          <span className="team-total">{t("common.of")} {clubs?.length || 0}</span>
        </div>
      </div>
      <div className="teams-link">
        {clubs
          .sort((a, b) => a.position - b.position)
          .slice(0, visibleTeams)
          .map(({ name, id }) => (<a key={id} onClick={() => selectClub(id)}>{name}</a>))}
      </div>
    </div>
  );

  if (draggable && id) {
    return (
      <Draggable draggableId={id.toString()} index={index}>
        {(provided) =>
          content({
            innerRef: provided.innerRef,
            draggableProps: provided.draggableProps,
            dragHandleProps: provided.dragHandleProps,
          })
        }
      </Draggable>
    );
  }

  return content();
};
